import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, C } from './MobileShell';
import MaMark from './MaMark';
import { listSavedAppAccounts } from './savedAppAccounts';

const inputCls = 'w-full rounded-lg px-4 py-3 text-sm outline-none';
const inputStyle = { background: C.card, border: `1px solid ${C.border}`, color: C.text };

function istVerwaltungskonto(account) {
  if ((account?.areas || []).includes('verwaltung')) return true;
  return String(account?.email || '').trim().toLowerCase().startsWith('s-admin@');
}

function errorText(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  if (err?.code === 'ERR_NETWORK') return 'Keine Verbindung zum Server';
  if (err?.code === 'ECONNABORTED') return 'Zeitüberschreitung — App unter https://mamv.info/m öffnen, nicht über localhost.';
  return 'Fehler bei der Anmeldung';
}

export default function MobileLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { login } = useMobile();
  const initialMode = params.get('mode') === 'register' ? 'register' : 'login';
  const initialEmail = params.get('email') || '';
  const [mode, setMode] = useState(initialMode);
  const [sadmin, setSadmin] = useState(false);
  const [form, setForm] = useState({
    email: initialEmail, password: '', first_name: '', last_name: '', code: '', pin: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const saved = listSavedAppAccounts().filter((a) => sadmin || !istVerwaltungskonto(a));
  const logoTaps = useRef({ count: 0, timer: null });

  useEffect(() => () => {
    if (logoTaps.current.timer) clearTimeout(logoTaps.current.timer);
  }, []);

  const onLogoTap = () => {
    const taps = logoTaps.current;
    taps.count += 1;
    if (taps.timer) clearTimeout(taps.timer);
    if (taps.count >= 3) {
      taps.count = 0;
      setSadmin((on) => !on);
      setMode('login');
      return;
    }
    taps.timer = setTimeout(() => { taps.count = 0; }, 2000);
  };

  useEffect(() => {
    const email = params.get('email') || '';
    const nextMode = params.get('mode') === 'register' ? 'register' : 'login';
    setMode(nextMode);
    if (nextMode === 'register') setSadmin(false);
    if (email) setForm((p) => ({ ...p, email }));
  }, [params]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = mode === 'login'
        ? await mapi.post('/app/login', { email: form.email, password: form.password })
        : await mapi.post('/app/register', {
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
          code: form.code,
          pin: form.pin,
        });
      if (mode === 'login' && sadmin) localStorage.setItem('app_area', 'verwaltung');
      login(r.data);
      toast.success(mode === 'login' ? 'Willkommen zurück!' : 'Konto erstellt!');
      // Ein Konto, das mit einer Lehrkraft oder einer Rolle verknüpft ist, hat seinen Bereich
      // schon aus dem Datensatz — nur wer gar keinen hat, braucht Kennung und PIN.
      const hatZugriff = Boolean(r.data.me?.identities?.length || r.data.me?.areas?.length);
      nav(hatZugriff ? '/m' : '/m/settings/identities');
    } catch (err) {
      const msg = errorText(err);
      const exists = err?.response?.status === 409;
      if (exists && mode === 'register') {
        setMode('login');
        setError('Dieses Konto gibt es schon — bitte anmelden (z. B. mit dem Start-Passwort der Lehrkraft).');
      } else {
        setError(msg);
      }
      toast.error(exists ? 'Konto existiert bereits' : msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobileShell bottomPad={false} bottomNav={null}>
      <div className="flex-1 flex flex-col justify-center px-6 py-10">
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 overflow-hidden"
               onClick={onLogoTap}
               style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <MaMark className="w-14 h-14" testId="mobile-login-logo" />
          </div>
          <h1 className="text-xl font-bold">Musik Akademie</h1>
          <p className="text-sm" style={{ color: C.dim }}>
            {mode === 'register' ? 'Neue Registrierung' : 'Anmelden'}
          </p>
        </div>

        {mode === 'login' && sadmin && (
          <p className="text-xs mb-3 text-center" style={{ color: C.dim }}>
            Angemeldet als <span className="font-semibold" style={{ color: C.text }}>s-admin</span>
            {' · '}Verwaltung · alle Bereiche
          </p>
        )}

        {mode === 'login' && saved.length > 0 && (
          <div className="mb-4 space-y-1.5" data-testid="mobile-login-saved-accounts">
            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.dim }}>
              Bekannte Konten
            </p>
            {saved.slice(0, 5).map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setForm((p) => ({ ...p, email: a.email || '' }))}
                data-testid={`mobile-login-saved-${a.id}`}
                className="w-full text-left rounded-xl px-3 py-2.5 active:opacity-80"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <span className="block text-sm font-medium truncate">{a.full_name || a.email}</span>
                {a.email && (
                  <span className="block text-[11px] truncate" style={{ color: C.dim }}>{a.email}</span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="flex rounded-lg overflow-hidden mb-6" style={{ border: `1px solid ${C.border}` }}>
          {['login', 'register'].map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type={active ? 'submit' : 'button'}
                form="mobile-auth-form"
                disabled={busy}
                onClick={() => {
                  if (!active) {
                    setMode(m);
                    if (m === 'register') setSadmin(false);
                  }
                }}
                data-testid={active ? 'mobile-auth-submit' : `mobile-auth-tab-${m}`}
                className="flex-1 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
                style={{ background: active ? C.red : C.card, color: active ? '#fff' : C.text }}
              >
                {busy && active ? 'Bitte warten…' : (m === 'login' ? 'Anmelden' : 'Registrieren')}
              </button>
            );
          })}
        </div>

        <form id="mobile-auth-form" onSubmit={submit} className="space-y-3">
          {mode === 'register' && (
            <div className="flex gap-3">
              <input required placeholder="Vorname" value={form.first_name} onChange={set('first_name')}
                     className={inputCls} style={inputStyle} data-testid="mobile-register-firstname" />
              <input required placeholder="Nachname" value={form.last_name} onChange={set('last_name')}
                     className={inputCls} style={inputStyle} data-testid="mobile-register-lastname" />
            </div>
          )}
          <input required type="text" autoComplete="username"
                 placeholder={sadmin && mode === 'login' ? 'E-Mail oder Benutzername (s-admin)' : 'E-Mail oder Benutzername'}
                 value={form.email} onChange={set('email')}
                 className={inputCls} style={inputStyle} data-testid="mobile-auth-email" />
          <input required type="password" placeholder={mode === 'register' ? 'Passwort (min. 8 Zeichen)' : 'Passwort'}
                 minLength={mode === 'register' ? 8 : undefined}
                 value={form.password} onChange={set('password')}
                 className={inputCls} style={inputStyle} data-testid="mobile-auth-password" />
          {mode === 'register' && (
            <div className="flex gap-3">
              <input required placeholder="Kennung" value={form.code} onChange={set('code')}
                     autoComplete="off" className={inputCls} style={inputStyle}
                     data-testid="mobile-register-code" />
              <input required placeholder="PIN" value={form.pin} onChange={set('pin')}
                     autoComplete="off" inputMode="numeric" className={inputCls} style={inputStyle}
                     data-testid="mobile-register-pin" />
            </div>
          )}
          {error && (
            <p className="text-sm text-center px-2 py-2 rounded-lg" data-testid="mobile-auth-error"
               style={{ background: C.rose, color: C.red }}>{error}</p>
          )}
        </form>

        {mode === 'register' && (
          <p className="text-xs mt-4 text-center" style={{ color: C.dim }}>
            Registrierung nur mit Kennung und PIN. Die Kennung legt fest,
            ob das Konto für Lehrer, Schüler oder Eltern gilt.
          </p>
        )}

        <p className="text-xs mt-6 text-center" style={{ color: C.dim }}>
          <a href="/login" className="underline" style={{ color: C.text }}>Desktop-Verwaltung</a>
          {' · '}
          <a href="/portal/login" className="underline" style={{ color: C.text }}>Eltern-Portal</a>
        </p>
      </div>
    </MobileShell>
  );
}
